import { useParams, useNavigate, Link } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import Navbar from "../components/Navbar";
import { Country, State, City } from "country-state-city";
import Swal from "sweetalert2";
import Footer from "../components/Footer";
import ChatbotWidget from "../components/chatbot/ChatbotWidget";
import { Helmet } from "react-helmet-async";

const API_BASE = "https://swiftintelli.com/api";

/* ============================================================
   SEO CONFIGURATION
============================================================ */

const COURSE_SEO = {
  "ai-ml-with-applied-gen-ai": {
    title: "Enroll in AI/ML with Applied Gen-AI | SWIFTIntelli",
    description:
      "Join SWIFTIntelli's 6-month AI/ML program. Learn Python, Machine Learning, Deep Learning, and MLOps through hands-on labs and real-world projects. Enroll now!",
  },

  "prompt-engineering-and-applied-gen-ai": {
    title:
      "Enroll in Prompt Engineering & Applied Gen-AI | SWIFTIntelli",
    description:
      "Master Prompt Engineering, LangChain, RAG systems, and AI Agents with SWIFTIntelli. Build production-ready AI workflows through expert guidance. Register today!",
  },
};

/* ============================================================
   SLUG HELPER
============================================================ */

function prettifySlug(slug) {
  if (!slug) return "";

  return slug
    .replace(/-/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

/* ============================================================
   COMPONENT
============================================================ */

export default function EnrollForm({
  isModal = false,
  onClose,
}) {
  const { slug } = useParams();
  const navigate = useNavigate();

  /* ============================================================
     STATES
  ============================================================ */

  const [loading, setLoading] = useState(false);

  const [submitted, setSubmitted] =
    useState(false);

  const [courseData, setCourseData] =
    useState(null);

  const [courseLoading, setCourseLoading] =
    useState(true);

  const [errors, setErrors] =
    useState({});

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    country: "IN",
    state: "",
    city: "",
    status: "",
    course: "",
    experience: "",
    modeOfLearning: "",
    message: "",
  });

  /* ============================================================
     COUNTRY DATA
  ============================================================ */

  const countries =
    Country.getAllCountries();

  const selCountry =
    Country.getCountryByCode(
      form.country
    );

  const phoneCode =
    selCountry?.phonecode || "91";

  const flag =
    selCountry?.flag || "🌐";

  const states =
    State.getStatesOfCountry(
      form.country
    );

  const cities =
    City.getCitiesOfState(
      form.country,
      form.state
    );

  /* ============================================================
     DYNAMIC COURSE DETAILS
  ============================================================ */

  const courseName =
    courseData?.title ||
    courseData?.name ||
    prettifySlug(slug) ||
    "Course";

  const courseSlug =
    courseData?.slug || slug;

  /* ============================================================
     DYNAMIC SEO
  ============================================================ */

  const seoData =
    COURSE_SEO[slug] || {
      title: `Enroll in ${courseName} | SWIFTIntelli`,

      description: `Enroll in ${courseName} at SWIFTIntelli and build practical, industry-ready technology skills through hands-on learning and expert guidance.`,
    };

  /* ============================================================
     LOAD EMAIL
  ============================================================ */

  useEffect(() => {
    const storedEmail =
      localStorage.getItem("email") || "";

    setForm((previous) => ({
      ...previous,
      email: storedEmail,
    }));
  }, []);

  /* ============================================================
     FETCH COURSE BY SLUG
  ============================================================ */

  useEffect(() => {
    if (!slug) {
      setCourseLoading(false);
      return;
    }

    let cancelled = false;

    const fetchCourse = async () => {
      try {
        setCourseLoading(true);

        const url =
          `${API_BASE}/getCourse.php?slug=` +
          encodeURIComponent(slug);

        console.log(
          "Fetching course:",
          url
        );

        const response =
          await fetch(url);

        const data =
          await response.json();

        console.log(
          "Course API response:",
          data
        );

        if (cancelled) return;

        /*
          Support multiple possible
          API response structures.
        */

        const resolvedCourse =
          data?.course ||
          data?.data ||
          data;

        setCourseData(
          resolvedCourse
        );

        const resolvedTitle =
          resolvedCourse?.title ||
          resolvedCourse?.name ||
          prettifySlug(slug);

        setForm((previous) => ({
          ...previous,
          course: resolvedTitle,
        }));
      } catch (error) {
        console.error(
          "Failed to load course:",
          error
        );

        if (!cancelled) {
          /*
            Fallback to slug
          */

          const fallbackCourse =
            prettifySlug(slug);

          setForm((previous) => ({
            ...previous,
            course: fallbackCourse,
          }));
        }
      } finally {
        if (!cancelled) {
          setCourseLoading(false);
        }
      }
    };

    fetchCourse();

    return () => {
      cancelled = true;
    };
  }, [slug]);

  /* ============================================================
     HANDLE INPUT CHANGE
  ============================================================ */

  const handleChange =
    useCallback(
      (e) => {
        const {
          name,
          value,
        } = e.target;

        setErrors(
          (previous) => ({
            ...previous,
            [name]: "",
          })
        );

        /* NAME */

        if (name === "name") {
          const cleanValue =
            value
              .replace(
                /[^A-Za-z ]/g,
                ""
              )
              .replace(
                /\s{2,}/g,
                " "
              )
              .replace(
                /^\s/,
                ""
              )
              .slice(0, 50);

          setForm(
            (previous) => ({
              ...previous,
              name: cleanValue,
            })
          );

          return;
        }

        /* PHONE */

        if (name === "phone") {
          const currentPhoneCode =
            Country.getCountryByCode(
              form.country
            )?.phonecode || "";

          const maxLength =
            currentPhoneCode === "91" ||
            currentPhoneCode === "1"
              ? 10
              : currentPhoneCode === "44"
              ? 11
              : 15;

          setForm(
            (previous) => ({
              ...previous,

              phone: value
                .replace(/\D/g, "")
                .slice(
                  0,
                  maxLength
                ),
            })
          );

          return;
        }

        /* COUNTRY */

        if (name === "country") {
          setForm(
            (previous) => ({
              ...previous,

              country: value,
              state: "",
              city: "",
              phone: "",
            })
          );

          return;
        }

        /* STATE */

        if (name === "state") {
          setForm(
            (previous) => ({
              ...previous,

              state: value,
              city: "",
            })
          );

          return;
        }

        /* MESSAGE */

        if (name === "message") {
          setForm(
            (previous) => ({
              ...previous,

              message:
                value.replace(
                  /^\s/,
                  ""
                ),
            })
          );

          return;
        }

        /* DEFAULT */

        setForm(
          (previous) => ({
            ...previous,
            [name]: value,
          })
        );
      },
      [form.country]
    );

  /* ============================================================
     VALIDATION
  ============================================================ */

  const validate = () => {
    const validationErrors = {};

    /* NAME */

    if (!form.name.trim()) {
      validationErrors.name =
        "Full name is required";
    } else if (
      form.name.trim().length < 2
    ) {
      validationErrors.name =
        "Name must be at least 2 characters";
    }

    /* EMAIL */

    if (!form.email.trim()) {
      validationErrors.email =
        "Email is required";
    }

    /* PHONE */

    if (!form.phone) {
      validationErrors.phone =
        "Phone number is required";
    } else {
      const countryCode =
        selCountry?.phonecode || "";

      const requiredLength =
        countryCode === "91" ||
        countryCode === "1"
          ? 10
          : countryCode === "44"
          ? 11
          : 15;

      if (
        form.phone.length !==
        requiredLength
      ) {
        validationErrors.phone =
          `Phone must be ${requiredLength} digits`;
      }
    }

    /* COUNTRY */

    if (!form.country) {
      validationErrors.country =
        "Please select a country";
    }

    /* STATE */

    if (!form.state) {
      validationErrors.state =
        "Please select a state";
    }

    /* CITY */

    if (!form.city) {
      validationErrors.city =
        "Please select a city";
    }

    /* STATUS */

    if (!form.status) {
      validationErrors.status =
        "Please select your status";
    }

    /* EXPERIENCE */

    if (!form.experience) {
      validationErrors.experience =
        "Please select experience level";
    }

    /* MODE */

    if (!form.modeOfLearning) {
      validationErrors.modeOfLearning =
        "Please select a mode";
    }

    return validationErrors;
  };

  /* ============================================================
     SUBMIT
  ============================================================ */

  const submit =
    async () => {
      const validationErrors =
        validate();

      if (
        Object.keys(
          validationErrors
        ).length > 0
      ) {
        setErrors(
          validationErrors
        );

        const firstField =
          Object.keys(
            validationErrors
          )[0];

        document
          .querySelector(
            `[name="${firstField}"]`
          )
          ?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });

        return;
      }

      setLoading(true);

      try {
        const stateName =
          states.find(
            (state) =>
              state.isoCode ===
              form.state
          )?.name;

        /* =====================================================
           ENROLLMENT API
        ===================================================== */

        const response =
          await fetch(
            `${API_BASE}/enroll.php`,
            {
              method: "POST",

              headers: {
                "Content-Type":
                  "application/json",
              },

              body:
                JSON.stringify({
                  ...form,

                  course_id:
                    courseData?.id,

                  courseSlug:
                    courseSlug,

                  phoneCode,

                  countryName:
                    selCountry?.name,

                  stateName,
                }),
            }
          );

        const responseText =
          await response.text();

        const data =
          JSON.parse(
            responseText
              .replace(
                /```php|```/g,
                ""
              )
              .trim()
          );

        /* =====================================================
           SUCCESS
        ===================================================== */

        if (
          data.status ===
          "success"
        ) {
          /*
            SEND EMAIL
          */

          await fetch(
            `${API_BASE}/sendEnrollMail.php`,
            {
              method: "POST",

              headers: {
                "Content-Type":
                  "application/json",
              },

              body:
                JSON.stringify({
                  name:
                    form.name,

                  email:
                    form.email,

                  phone:
                    `+${phoneCode} ${form.phone}`,

                  country:
                    selCountry?.name,

                  state:
                    stateName,

                  city:
                    form.city,

                  status:
                    form.status,

                  course:
                    form.course,

                  courseSlug,

                  experience:
                    form.experience,

                  modeOfLearning:
                    form.modeOfLearning,

                  message:
                    form.message,
                }),
            }
          );

          setSubmitted(true);

          Swal.fire({
            icon: "success",

            title:
              "🎉 Enrollment Successful!",

            text:
              `You have successfully enrolled for ${courseName}. We'll get in touch with you soon!`,

            confirmButtonColor:
              "#2563eb",

            confirmButtonText:
              "Great, Thanks!",

            allowOutsideClick:
              false,
          }).then(() => {
            if (
              isModal &&
              onClose
            ) {
              onClose();
            } else {
              navigate("/");
            }
          });
        }

        /* =====================================================
           ALREADY EXISTS
        ===================================================== */

        else if (
          data.status ===
          "exists"
        ) {
          Swal.fire({
            icon: "info",

            title:
              "Already Enrolled",

            text:
              data.message,

            confirmButtonColor:
              "#2563eb",
          });
        }

        /* =====================================================
           ERROR
        ===================================================== */

        else {
          Swal.fire({
            icon: "error",

            title:
              "Enrollment Failed",

            text:
              data.message ||
              "Something went wrong.",

            confirmButtonColor:
              "#2563eb",
          });
        }
      } catch (error) {
        console.error(
          "Enrollment error:",
          error
        );

        Swal.fire({
          icon: "error",

          title:
            "Server Error",

          text:
            "Something went wrong. Please try again.",
        });
      } finally {
        setLoading(false);
      }
    };

  /* ============================================================
     ERROR COMPONENT
  ============================================================ */

  const Err = ({
    field,
  }) =>
    errors[field] ? (
      <span className="ef-err">
        {errors[field]}
      </span>
    ) : null;

  /* ============================================================
     CHEVRON
  ============================================================ */

  const ChevronSVG = () => (
    <svg
      className="ef-chevron"
      width="12"
      height="7"
      viewBox="0 0 12 7"
      fill="none"
    >
      <path
        d="M1 1l5 5 5-5"
        stroke="#94a3b8"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  /* ============================================================
     FORM BODY
  ============================================================ */

  const formBody = (
    <div className="ef-card">

      {/* NAME + EMAIL */}

      <div className="ef-row">

        <div className="ef-field">

          <label className="ef-label">
            Full Name
            <span className="ef-star">
              {" "}*
            </span>
          </label>

          <input
            className={`ef-input${
              errors.name
                ? " ef-input-err"
                : ""
            }`}
            type="text"
            name="name"
            placeholder="Enter your full name"
            value={form.name}
            onChange={handleChange}
            autoComplete="name"
            maxLength={50}
          />

          <Err field="name" />

        </div>

        <div className="ef-field">

          <label className="ef-label">
            Email Address
            <span className="ef-star">
              {" "}*
            </span>
          </label>

          <div className="ef-email-wrap">

            <input
              className="ef-input ef-input-disabled"
              type="email"
              name="email"
              value={form.email}
              disabled
            />

            {form.email && (
              <span className="ef-verified-badge">
                ✓ Verified
              </span>
            )}

          </div>

        </div>

      </div>

      {/* COUNTRY + PHONE */}

      <div className="ef-row">

        <div className="ef-field">

          <label className="ef-label">
            Country
            <span className="ef-star">
              {" "}*
            </span>
          </label>

          <div className="ef-sel-wrap">

            <select
              className={`ef-select${
                errors.country
                  ? " ef-input-err"
                  : ""
              }`}
              name="country"
              value={form.country}
              onChange={handleChange}
            >
              {countries.map(
                (country) => (
                  <option
                    key={country.isoCode}
                    value={country.isoCode}
                  >
                    {country.name}
                  </option>
                )
              )}
            </select>

            <ChevronSVG />

          </div>

          <Err field="country" />

        </div>

        <div className="ef-field">

          <label className="ef-label">
            Phone Number
            <span className="ef-star">
              {" "}*
            </span>
          </label>

          <div
            className={`ef-phone-wrap${
              errors.phone
                ? " ef-phone-err"
                : ""
            }`}
          >

            <div className="ef-phone-prefix">

              <span className="ef-flag">
                {flag}
              </span>

              <span className="ef-vline" />

              <span className="ef-dial-code">
                +{phoneCode}
              </span>

            </div>

            <input
              className="ef-input ef-phone-input"
              type="tel"
              inputMode="numeric"
              placeholder="Phone number"
              name="phone"
              value={form.phone}
              onChange={handleChange}
            />

          </div>

          <Err field="phone" />

        </div>

      </div>

      {/* STATE + CITY */}

      <div className="ef-row">

        <div className="ef-field">

          <label className="ef-label">
            State
            <span className="ef-star">
              {" "}*
            </span>
          </label>

          <div className="ef-sel-wrap">

            <select
              className={`ef-select${
                errors.state
                  ? " ef-input-err"
                  : ""
              }`}
              name="state"
              value={form.state}
              onChange={handleChange}
            >

              <option value="">
                Select your state
              </option>

              {states.map(
                (state) => (
                  <option
                    key={state.isoCode}
                    value={state.isoCode}
                  >
                    {state.name}
                  </option>
                )
              )}

            </select>

            <ChevronSVG />

          </div>

          <Err field="state" />

        </div>

        <div className="ef-field">

          <label className="ef-label">
            City
            <span className="ef-star">
              {" "}*
            </span>
          </label>

          <div className="ef-sel-wrap">

            <select
              className={`ef-select${
                errors.city
                  ? " ef-input-err"
                  : ""
              }`}
              name="city"
              value={form.city}
              onChange={handleChange}
            >

              <option value="">
                Select your city
              </option>

              {cities.map(
                (city) => (
                  <option
                    key={city.name}
                    value={city.name}
                  >
                    {city.name}
                  </option>
                )
              )}

            </select>

            <ChevronSVG />

          </div>

          <Err field="city" />

        </div>

      </div>

      {/* STATUS + EXPERIENCE + MODE */}

      <div className="ef-row ef-row-3">

        <div className="ef-field">

          <label className="ef-label">
            Professional Status
            <span className="ef-star">
              {" "}*
            </span>
          </label>

          <div className="ef-sel-wrap">

            <select
              className="ef-select"
              name="status"
              value={form.status}
              onChange={handleChange}
            >
              <option value="">
                Select status
              </option>

              <option value="Working Professional">
                Working Professional
              </option>

              <option value="Student">
                Student
              </option>

              <option value="Fresher">
                Fresher
              </option>

            </select>

            <ChevronSVG />

          </div>

          <Err field="status" />

        </div>

        <div className="ef-field">

          <label className="ef-label">
            Experience
            <span className="ef-star">
              {" "}*
            </span>
          </label>

          <div className="ef-sel-wrap">

            <select
              className="ef-select"
              name="experience"
              value={form.experience}
              onChange={handleChange}
            >

              <option value="">
                Select level
              </option>

              <option value="No prior experience">
                No prior experience
              </option>

              <option value="Beginner">
                Beginner
              </option>

              <option value="Intermediate">
                Intermediate
              </option>

            </select>

            <ChevronSVG />

          </div>

          <Err field="experience" />

        </div>

        <div className="ef-field">

          <label className="ef-label">
            Mode of Learning
            <span className="ef-star">
              {" "}*
            </span>
          </label>

          <div className="ef-sel-wrap">

            <select
              className="ef-select"
              name="modeOfLearning"
              value={form.modeOfLearning}
              onChange={handleChange}
            >

              <option value="">
                Select mode
              </option>

              <option value="Online">
                Online
              </option>

              <option value="Offline">
                Offline
              </option>

            </select>

            <ChevronSVG />

          </div>

          <Err field="modeOfLearning" />

        </div>

      </div>

      {/* MESSAGE */}

      <div className="ef-field ef-field-full">

        <label className="ef-label">
          Message
          <span className="ef-optional">
            {" "}(optional)
          </span>
        </label>

        <textarea
          className="ef-textarea"
          name="message"
          placeholder="Describe your learning goals or any specific requirements..."
          value={form.message}
          onChange={handleChange}
        />

      </div>

      {/* SUBMIT */}

      <button
        type="button"
        className="ef-submit-btn"
        onClick={submit}
        disabled={
          loading ||
          submitted
        }
      >
        {loading ? (
          <>
            <span className="ef-spinner" />
            {" "}Submitting...
          </>
        ) : submitted ? (
          "Enrolled ✓"
        ) : (
          "Submit Application →"
        )}
      </button>

    </div>
  );

  /* ============================================================
     MODAL MODE
  ============================================================ */

  if (isModal) {
    return (
      <div
        className="ef-overlay"
        onClick={onClose}
      >
        <div
          className="ef-modal-box"
          onClick={(e) =>
            e.stopPropagation()
          }
        >
          {formBody}
        </div>
      </div>
    );
  }

  /* ============================================================
     MAIN PAGE
  ============================================================ */

  return (
    <>
      {/* =====================================================
          DYNAMIC SEO
      ===================================================== */}

      <Helmet>

        <title>
          {seoData.title}
        </title>

        <meta
          name="description"
          content={seoData.description}
        />

        <link
          rel="canonical"
          href={`https://www.swiftintelli.com/enroll/courses/${slug}`}
        />

      </Helmet>

      <Navbar />

      {/* =====================================================
          PAGE HEADER
      ===================================================== */}

      <header className="blog-masthead">

        <div className="blog-masthead-overlay" />

        <div className="blog-masthead-content">

          {/* BREADCRUMB */}

          <div className="blog-breadcrumb">

            <Link to="/">
              Home
            </Link>

            <span>/</span>

            <Link to="/courses">
              Courses
            </Link>

            <span>/</span>

            {courseLoading ? (
              <span>
                Loading...
              </span>
            ) : (
              <Link
                to={`/courses/${slug}`}
              >
                {courseName}
              </Link>
            )}

            <span>/</span>

            <span>
              Enroll Now
            </span>

          </div>

          {/* DYNAMIC HEADER */}

          <h1>
            {courseLoading
              ? "Enroll Now"
              : `Enroll in ${courseName}`}
          </h1>

        </div>

      </header>

      {/* =====================================================
          MAIN CONTENT
      ===================================================== */}

      <main className="si-enroll-page">

        <div className="si-enroll-background" />

        <div className="si-enroll-container">

          {/* LEFT SIDE */}

          <section className="si-enroll-intro">

            <div className="si-enroll-label">
              <span />
              SWIFTIntelli
            </div>

            <h1>
              Start your
              <span>
                {" "}learning journey.
              </span>
            </h1>

            <p className="si-enroll-description">
              Take the next step toward building practical,
              industry-ready technology skills with
              SWIFTIntelli.
            </p>

            <div className="si-enroll-benefits">

              <div className="si-enroll-benefit">

                <div className="si-benefit-icon">
                  ✓
                </div>

                <div>

                  <h3>
                    Industry-Focused Learning
                  </h3>

                  <p>
                    Learn skills aligned with real-world
                    technology requirements.
                  </p>

                </div>

              </div>

              <div className="si-enroll-benefit">

                <div className="si-benefit-icon">
                  ✓
                </div>

                <div>

                  <h3>
                    Hands-On Projects
                  </h3>

                  <p>
                    Build practical projects and gain real
                    implementation experience.
                  </p>

                </div>

              </div>

              <div className="si-enroll-benefit">

                <div className="si-benefit-icon">
                  ✓
                </div>

                <div>

                  <h3>
                    Expert Guidance
                  </h3>

                  <p>
                    Learn with guidance from experienced
                    technology professionals.
                  </p>

                </div>

              </div>

            </div>

            <div className="si-enroll-note">

              <span>
                ✦
              </span>

              <p>
                Your information is safe with us.
                We will contact you regarding course
                details and enrollment.
              </p>

            </div>

          </section>

          {/* RIGHT SIDE */}

          <section className="si-enroll-form-section">

            {formBody}

          </section>

        </div>

      </main>

      <ChatbotWidget />

      <Footer />

    </>
  );
}